import { Maximize2 } from 'lucide-react';
import { useState } from 'react';
import { z } from 'zod';

import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@apps/web/components/ui/tooltip';
import {
    type AdminAgentArtifact,
    ArtifactEvidence,
    ArtifactFrame,
    ArtifactLifecycleStatusBadge,
} from '@apps/web/routes/_app/admin/agent/artifacts/frame';
import {
    type AdminArtifactDisplayMode,
    type AdminArtifactRendererProps,
} from '@apps/web/routes/_app/admin/agent/artifacts/shared';

const DAY_NAMES = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
] as const;
const DURATION_CLASSES = [
    'short',
    'standard',
    'long',
    'extended',
    'live_in',
] as const;

const durationClassSchema = z.enum([
    'all',
    'short',
    'standard',
    'long',
    'extended',
    'live_in',
]);

const shiftCalendarPayloadSchema = z
    .object({
        schema_version: z.literal(1),
        title: z.string().min(1),
        timezone: z.string().min(1),
        profile_summary: z.string().min(1),
        duration_classes: z.array(durationClassSchema),
        buckets: z.array(
            z.object({
                weekday: z.string().min(1),
                weekday_index: z.number().int().min(0).max(6),
                hour: z.number().int().min(0).max(23),
                duration_class: durationClassSchema,
                shift_count: z.number().int().positive(),
            }),
        ),
    })
    .superRefine((payload, context) => {
        const keys = new Set<string>();
        for (const [index, bucket] of payload.buckets.entries()) {
            const key = `${bucket.weekday_index}:${bucket.hour}:${bucket.duration_class}`;
            if (keys.has(key)) {
                context.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Calendar bucket keys must be unique.',
                    path: ['buckets', index],
                });
            }
            keys.add(key);
        }
    });

type ParsedShiftCalendarPayload = z.infer<typeof shiftCalendarPayloadSchema>;
type DurationSelection = 'all' | (typeof DURATION_CLASSES)[number];

export function AdminShiftCalendarArtifact({
    artifact,
    displayMode = 'full',
    onOpenArtifact,
}: AdminArtifactRendererProps) {
    const parsed = shiftCalendarPayloadSchema.safeParse(artifact.payload);
    if (!parsed.success) {
        return (
            <ArtifactFrame
                artifact={artifact}
                status={
                    <ArtifactLifecycleStatusBadge
                        lifecycleStatus={artifact.lifecycle_status}
                    />
                }
            >
                <ArtifactEvidence artifact={artifact} />
                <p className="text-destructive text-xs leading-relaxed">
                    This shift calendar artifact does not match the
                    admin.shift_calendar@1 contract, so the admin UI cannot
                    render it safely.
                </p>
            </ArtifactFrame>
        );
    }

    return (
        <ShiftCalendarView
            artifact={artifact}
            payload={parsed.data}
            displayMode={displayMode}
            onOpenArtifact={onOpenArtifact}
        />
    );
}

function ShiftCalendarView({
    artifact,
    payload,
    displayMode,
    onOpenArtifact,
}: {
    readonly artifact: AdminAgentArtifact;
    readonly payload: ParsedShiftCalendarPayload;
    readonly displayMode: AdminArtifactDisplayMode;
    readonly onOpenArtifact?: () => void;
}) {
    const [durationSelection, setDurationSelection] =
        useState<DurationSelection>('all');
    const selectedBuckets = payload.buckets.filter(
        (bucket) => bucket.duration_class === durationSelection,
    );
    const maxCount = Math.max(
        1,
        ...selectedBuckets.map((bucket) => bucket.shift_count),
    );
    const bucketByCell = new Map(
        selectedBuckets.map((bucket) => [
            `${bucket.weekday_index}:${bucket.hour}`,
            bucket,
        ]),
    );

    return (
        <ArtifactFrame
            artifact={artifact}
            title={payload.title}
            status={
                <ArtifactLifecycleStatusBadge
                    lifecycleStatus={artifact.lifecycle_status}
                />
            }
        >
            <ArtifactEvidence artifact={artifact} />
            <div className="space-y-3">
                <div className="space-y-1">
                    <p className="text-foreground text-xs font-medium">
                        Scheduling profile
                    </p>
                    <p className="text-muted-foreground text-xs leading-relaxed">
                        {payload.profile_summary}
                    </p>
                    <p className="text-muted-foreground font-mono text-[11px]">
                        {payload.timezone}
                    </p>
                </div>
                <div
                    aria-label="Shift duration filter"
                    className="flex flex-wrap gap-1.5"
                    role="group"
                >
                    <DurationFilterButton
                        active={durationSelection === 'all'}
                        label="All durations"
                        onClick={() => setDurationSelection('all')}
                    />
                    {payload.duration_classes
                        .filter((durationClass) => durationClass !== 'all')
                        .map((durationClass) => (
                            <DurationFilterButton
                                key={durationClass}
                                active={durationSelection === durationClass}
                                label={formatDurationClass(durationClass)}
                                onClick={() =>
                                    setDurationSelection(durationClass)
                                }
                            />
                        ))}
                </div>
                <div className="space-y-1.5">
                    <div className="text-muted-foreground flex justify-between text-[10px]">
                        <span>0 shifts</span>
                        <span>{maxCount} shifts in busiest cell</span>
                    </div>
                    <div
                        data-testid={
                            displayMode === 'inline'
                                ? 'shift-calendar-inline-scroll'
                                : undefined
                        }
                    >
                        <div
                            aria-label={`Shift calendar for ${payload.timezone}`}
                            className="grid gap-px"
                            data-testid="shift-calendar-grid"
                            style={{
                                gridTemplateColumns:
                                    'repeat(7, minmax(0, 1fr))',
                            }}
                        >
                            {DAY_NAMES.map((dayName) => (
                                <div
                                    key={dayName}
                                    className="text-muted-foreground truncate px-0.5 text-center text-[10px] font-medium"
                                    title={dayName}
                                >
                                    {dayName.slice(0, 3)}
                                </div>
                            ))}
                            {Array.from({ length: 24 }, (_, hour) =>
                                DAY_NAMES.map((_, dayIndex) => {
                                    const bucket = bucketByCell.get(
                                        `${dayIndex}:${hour}`,
                                    );
                                    const cellDetails = formatCellDetails({
                                        bucket,
                                        dayIndex,
                                        hour,
                                    });
                                    return (
                                        <Tooltip key={`${dayIndex}-${hour}`}>
                                            <TooltipTrigger asChild={true}>
                                                <div
                                                    aria-label={
                                                        cellDetails.ariaLabel
                                                    }
                                                    className="border-border/60 h-3 rounded-[2px] border outline-none focus-visible:ring-2 focus-visible:ring-[var(--aa-focus,#5533ee)]"
                                                    data-testid={`shift-calendar-cell-${dayIndex}-${hour}`}
                                                    style={{
                                                        backgroundColor: bucket
                                                            ? heatColor(
                                                                  bucket.shift_count,
                                                                  maxCount,
                                                              )
                                                            : 'var(--muted)',
                                                    }}
                                                    tabIndex={0}
                                                    title={cellDetails.title}
                                                />
                                            </TooltipTrigger>
                                            <TooltipContent
                                                className="space-y-0.5"
                                                side="top"
                                            >
                                                <p>{cellDetails.title}</p>
                                                {bucket ? (
                                                    <p className="text-primary-foreground/75">
                                                        Duration:{' '}
                                                        {formatDurationClass(
                                                            bucket.duration_class,
                                                        )}
                                                    </p>
                                                ) : null}
                                            </TooltipContent>
                                        </Tooltip>
                                    );
                                }),
                            )}
                        </div>
                    </div>
                    <div className="text-muted-foreground grid grid-cols-4 text-[10px]">
                        {[0, 6, 12, 18].map((hour) => (
                            <span key={hour}>{formatHour(hour)}</span>
                        ))}
                    </div>
                </div>
                <p className="text-muted-foreground text-[10px]">
                    Cells show pre-aggregated shift starts in the organization’s
                    local timezone. Select a duration class to isolate its
                    pattern.
                </p>
                {displayMode === 'inline' && onOpenArtifact ? (
                    <button
                        type="button"
                        className="inline-flex items-center gap-1.5 rounded-[3px] border border-[var(--aa-purple-border,#d8cffb)] bg-[var(--aa-purple-wash,#f7f5fe)] px-2 py-1 text-[11px] font-medium text-[var(--aa-purple-strong,#4629c7)] transition-colors hover:bg-[var(--aa-purple-soft,#eeeafd)] focus-visible:ring-2 focus-visible:ring-[var(--aa-focus,#5533ee)] focus-visible:outline-none"
                        onClick={onOpenArtifact}
                    >
                        <Maximize2 className="size-3" />
                        Open in Artifacts
                    </button>
                ) : null}
            </div>
        </ArtifactFrame>
    );
}

function formatCellDetails({
    bucket,
    dayIndex,
    hour,
}: {
    readonly bucket?: ParsedShiftCalendarPayload['buckets'][number];
    readonly dayIndex: number;
    readonly hour: number;
}): { readonly ariaLabel: string; readonly title: string } {
    const dayName = bucket?.weekday ?? DAY_NAMES[dayIndex];
    const count = bucket?.shift_count ?? 0;
    const title = `${dayName} ${formatHour(hour)} — ${count} shifts`;
    return {
        ariaLabel: `${dayName} ${formatHour(hour)}: ${count} shifts`,
        title,
    };
}

function DurationFilterButton({
    active,
    label,
    onClick,
}: {
    readonly active: boolean;
    readonly label: string;
    readonly onClick: () => void;
}) {
    return (
        <button
            aria-pressed={active}
            className={
                active
                    ? 'bg-foreground text-background rounded px-2 py-1 text-[10px] font-medium'
                    : 'border-border text-muted-foreground hover:text-foreground rounded border px-2 py-1 text-[10px]'
            }
            onClick={onClick}
            type="button"
        >
            {label}
        </button>
    );
}

function formatDurationClass(value: string): string {
    return value.replace('_', ' ');
}

function formatHour(hour: number): string {
    return `${String(hour).padStart(2, '0')}:00`;
}

function heatColor(count: number, maximum: number): string {
    const opacity = 0.12 + (count / maximum) * 0.78;
    return `color-mix(in srgb, var(--primary) ${Math.round(opacity * 100)}%, transparent)`;
}
